/**
 * Loans Table Columns Configuration
 */
import React from 'react';
import { StatusBadge } from '../common/StatusBadge';
import { formatCurrency } from '../../lib/formatters';
import { Clock } from 'lucide-react';

function formatLoanDate(value) {
  if (!value) return null;
  return new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function getLoansColumns() {
  return [
    {
      key: 'loan_number',
      label: 'Loan #',
      className: 'w-[22%]',
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.loan_number}</span>
          <span className="text-xs text-muted-foreground">{row.product_name || '-'}</span>
        </div>
      ),
    },
    {
      key: 'borrower_name',
      label: 'Borrower',
      className: 'w-[22%]',
      render: (row) => {
        const sanctionedOn = formatLoanDate(row.sanction_date);
        return (
          <div className="flex flex-col">
            <span>{row.borrower_name || '-'}</span>
            <span className="text-xs text-muted-foreground">
              {sanctionedOn ? `sanctioned on ${sanctionedOn}` : 'not sanctioned'}
            </span>
          </div>
        );
      },
    },
    {
      key: 'sanctioned_amount',
      label: 'Sanctioned',
      className: 'w-[16%] tabular-nums',
      render: (row) => formatCurrency(row.sanctioned_amount),
    },
    {
      key: 'total_outstanding',
      label: 'Outstanding',
      className: 'w-[16%] tabular-nums',
      render: (row) => formatCurrency(row.total_outstanding || 0),
    },
    {
      key: 'status',
      label: 'Status',
      className: 'w-[12%] text-center pr-8',
      render: (row) => (
        <div className="flex items-center justify-center gap-1">
          <StatusBadge status={row.is_frozen ? 'Frozen' : row.status} className='whitespace-nowrap' />
          {row.cooling_off_status === 'active' && (
            <Clock className="h-3 w-3 text-emerald-500 animate-pulse " data-testid={`cooling-indicator-${row.id}`} />
          )}
        </div>
      ),
    },
  ];
}

function toDateString(date) {
  if (date instanceof Date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }
  return date;
}

function matchesSearchTerm(loan, searchTerm) {
  if (!searchTerm) return true;
  const term = searchTerm.toLowerCase();
  return (
    loan.borrower_name?.toLowerCase().includes(term) ||
    loan.loan_number?.toLowerCase().includes(term) ||
    (loan.is_creation_draft && String(loan.draft_application_id || '').includes(term))
  );
}

function matchesDateFilter(loan, dateFrom, dateTo) {
  // Creation drafts have no sanction date — always keep them visible.
  if (!loan.sanction_date) return true;
  return !(dateFrom && loan.sanction_date < toDateString(dateFrom)) &&
    !(dateTo && loan.sanction_date > toDateString(dateTo));
}

function matchesAmountFilter(loan, minAmount, maxAmount) {
  return !(minAmount && loan.sanctioned_amount < parseFloat(minAmount)) &&
    !(maxAmount && loan.sanctioned_amount > parseFloat(maxAmount));
}

export function filterLoans(loans, filters) {
  const { searchTerm, statusFilter, loanTypeFilter, dateFrom, dateTo, minAmount, maxAmount } = filters;
  
  return loans.filter((loan) => {
    return matchesSearchTerm(loan, searchTerm) &&
      (statusFilter === 'all' || loan.status === statusFilter) &&
      (loanTypeFilter === 'all' || String(loan.loan_type_id) === loanTypeFilter) &&
      matchesDateFilter(loan, dateFrom, dateTo) &&
      matchesAmountFilter(loan, minAmount, maxAmount);
  });
}

export const INITIAL_LOAN_FORM = {
  loan_application_id: '',
  loan_type_id: '',
  borrower_name: '',
  borrower_email: '',
  borrower_phone: '',
  borrower_address: '',
  borrower_id_type: 'Aadhar',
  borrower_id_number: '',
  sanctioned_amount: '',
  rate_of_interest: '',
  tenure_months: '',
  tenure_years: '',
  tenure_extra_months: '',
  repayment_start_date: '',
  remarks: '',
};
